/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useState } from 'react';
import Datatable from '@/components/common/Datatable';
import {
  Box,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import cartIcon from '../../../assets/svg/shopping-bag.svg?react';
import wishListIcon from '../../../assets/svg/wishlistIcon.svg?react';
import { FiMoreVertical } from 'react-icons/fi';
import eyeIcon from '../../../assets/svg/eye.svg?react';
import editIcon from '../../../assets/svg/editIcon.svg?react';
import deleteIcon from '../../../assets/svg/delete.svg?react';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import CustomizedIcon from '../../../assets/svg/customizable-icon.svg?react';
import restockIcon from '../../../assets/svg/restock.svg?react';
import { useDeleteProduct, useGetProducts, useRestockVariant } from '@/services/manageProduct';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import { AxiosError } from 'axios';
import useCustomToast from '@/components/common/Toast';
import useErrorHandler from '@/utils/errorHandler';
import { convertDateFormat } from '@/utils/convertDateFormat';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { useNavigate } from 'react-router-dom';
import FormModal from '@/components/common/FormModal';
import { Column, Row } from 'react-table';
import Button from '@/components/common/Button';
import RestockForm from '@/components/common/RestockForm';
import { IProduct } from '@/services/manageProduct/types';
import { AddonTabProps } from '@/services/manageAddons/types';

export interface IVariant {
  id: number;
  color: string;
  value: string;
  size: string;
  variant_price: number;
  stock_quantity: number;
}

export const ActiveAddOns = ({ filters, setFilters }: AddonTabProps) => {
  const [selectedId, setSelectedId] = useState<number | null>(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isRestockOpen, onOpen: onRestockOpen, onClose: onRestockClose } = useDisclosure();
  const [selectedProduct, setSelectProduct] = useState<IProduct | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [search, setQuery] = useState('');
  const [variants, setVariants] = useState<IVariant[]>([]);
  const { showToast } = useCustomToast();
  const { handleError } = useErrorHandler();
  const navigate = useNavigate();

  const {
    data: Products,
    refetch,
    isLoading,
    isError,
  } = useGetProducts({
    status: 'approved',
    page: pagination.page,
    query: search,
    is_add_on: true,
    ...filters,
  });

  const { mutateAsync: deleteProduct, isLoading: isDeleting } = useDeleteProduct();

  const { mutateAsync: restockVariant, isLoading: isRestockLoading } = useRestockVariant();
  const handleRestockSubmit = () => {
    try {
      restockVariant({
        product_id: selectedId?.toString() ?? '',
        data: variants,
      }).then(() => {
        setSelectedId(null);
        onRestockClose();
        showToast('success', 'Restock Successful');
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        handleError(error);
      }
    }
  };

  useEffect(() => {
    if (Products) {
      setPagination((prev) => ({
        ...prev,
        pageSize: Products.pageSize,
        totalCount: Products.count,
        totalPages: Products.page_count,
      }));
    }
  }, [Products, refetch]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleSearch = (searchTerm: string) => {
    setQuery(searchTerm);
    setPagination((prev) => ({ ...prev, page: 1 }));
    refetch();
  };

  const applyFilters = () => {
    refetch();
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      category: '',
      // subcategory: '',
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };
  const openModal = (id: number) => {
    setSelectedId(id);
    onOpen();
  };

  const openRestockModal = (product: IProduct) => {
    setSelectedId(product.id);
    setSelectProduct(product);
    onRestockOpen();
  };

  const handleDeleteConfirm = () => {
    if (selectedId !== null) {
      try {
        deleteProduct(selectedId).then(() => {
          setSelectedId(null);
          onClose();
          showToast('success', 'Addon deleted Successfully');
        });
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          handleError(error);
        }
      }
    } else return;
  };

  useEffect(() => {
    if (
      filters.start_date === '' &&
      filters.end_date === '' &&
      filters.category === ''
      // filters.subcategory === ''
    ) {
      refetch();
    }
  }, [filters, pagination.page]);

  const columns: Column<IProduct>[] = useMemo(
    () => [
      {
        Header: 'Addon ID',
        accessor: 'id',
      },
      {
        Header: 'Addon Name',
        accessor: 'name',
        Cell: ({ row }: { row: Row<IProduct> }) => (
          <HStack gap={2} alignItems={'end'} minWidth={'300px'}>
            <Image
              h={'48px'}
              w={'48px'}
              minW="48px"
              maxW="48px"
              maxH="48px"
              borderRadius={'lg'}
              src={
                row.original.primary_image ? row.original.primary_image.image_small : '' // or use a placeholder SVG/component
              }
              alt={row.original.name}
              objectFit={'cover'}
              objectPosition={'top'}
            />
            <VStack gap={1} align="start" justifyContent="end" h="full">
              <Text
                fontSize="sm"
                fontWeight="medium"
                maxW={{ base: '200px', md: '300px', lg: '400px' }}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                title={row.original.name}
              >
                {row.original.name}
              </Text>
              <HStack gap={1}>
                {row.original.is_customizable && (
                  <HStack bg={'#F2F4F8'} borderRadius={'3px'} p={1}>
                    <Box p="2px" display="flex" alignItems="end">
                      <Icon as={CustomizedIcon} color={'brand.primary.DEFAULT'} />
                    </Box>
                  </HStack>
                )}

                <HStack bg={'#F2F4F8'} borderRadius={'3px'} p={1}>
                  <Box p="2px" display="flex" alignItems="end">
                    <Icon as={cartIcon} color={'brand.primary.DEFAULT'} />
                    <Text ml="4px" lineHeight={0.8} color={'text.secondary'}>
                      {row.original.cart_item_count ?? 0}
                    </Text>
                  </Box>
                  <Box p="2px" display="flex" alignItems="end">
                    <Icon as={wishListIcon} color={'brand.primary.DEFAULT'} />
                    <Text ml="4px" lineHeight={0.8} color={'text.secondary'}>
                      {row.original.wishlist_count ?? 0}
                    </Text>
                  </Box>
                </HStack>
              </HStack>
            </VStack>
          </HStack>
        ),
      },
      {
        Header: 'Category',
        accessor: 'sub_category',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const category = row.original.category;
          const subCategory = row.original.sub_category;

          const displayValue = subCategory || category || '—';

          return displayValue ? (
            <Text minWidth={'200px'}>{displayValue}</Text>
          ) : (
            <Divider orientation="horizontal" width={'20px'} borderColor={'text.primary'} />
          );
        },
      },
      {
        Header: 'Stock',
        accessor: 'stock_quantity',
      },
      {
        Header: 'Sold',
        accessor: 'sold_quantity',
        Cell: ({ row }: { row: Row<IProduct> }) => <Text>{row.original.sold_quantity ?? 0}</Text>,
      },
      {
        Header: 'Last Updated',
        accessor: 'updated_at',
        Cell: ({ row }: { row: Row<IProduct> }) => {
          const updatedAt = row.original.updated_at;
          return <Text minWidth={'100px'}>{convertDateFormat(updatedAt)}</Text>;
        },
      },
      {
        id: 'actions',
        Header: 'Actions',
        Cell: ({ row }: { row: Row<IProduct> }) => (
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<FiMoreVertical color="brand.primary.DEFAULT" />}
              variant=""
            />
            <MenuList>
              <MenuItem
                onClick={() =>
                  navigate(
                    NAVIGATION_ROUTES.VIEW_ADDONS.replace(':addonId', String(row.original.id)),
                  )
                }
              >
                <Flex gap={2} align="center">
                  <Icon as={eyeIcon} boxSize={4} color={'text.primary'} />
                  <Text color={'text.primary'} fontSize={'sm'} mt={1}>
                    View Detail
                  </Text>
                </Flex>
              </MenuItem>
              <MenuItem
                onClick={() =>
                  navigate(
                    NAVIGATION_ROUTES.EDIT_ADDONS.replace(':addonId', String(row.original.id)),
                    {
                      state: { fromTab: 0 },
                    },
                  )
                }
              >
                <Flex gap={2} align="center">
                  <Icon as={editIcon} boxSize={4} color={'text.primary'} />
                  <Text color={'text.primary'} fontSize={'sm'} mt={1}>
                    Edit
                  </Text>
                </Flex>
              </MenuItem>
              <MenuItem onClick={() => openRestockModal(row.original)}>
                <Flex gap={2} align="center">
                  <Icon as={restockIcon} boxSize={4} color={'text.primary'} />
                  <Text color={'text.primary'} fontSize={'sm'} mt={1}>
                    Restock
                  </Text>
                </Flex>
              </MenuItem>
              <MenuItem onClick={() => openModal(row.original.id)}>
                <Flex gap={2} align="center">
                  <Icon as={deleteIcon} boxSize={4} color={'status.error'} />
                  <Text color={'status.error'} mt={1}>
                    Delete
                  </Text>
                </Flex>
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      },
    ],
    [Products],
  );
  return (
    <Box bg={'background.white'}>
      <Datatable
        data={Products?.results ?? []}
        isLoading={isLoading}
        isError={isError}
        columns={columns}
        headerButton={
          <Button onClick={() => navigate(NAVIGATION_ROUTES.ADD_ADDONS)}>+ Add Addon</Button>
        }
        pagination={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalCount: pagination.totalCount,
          totalPages: pagination.totalPages,
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        filters={filters}
        setFilters={setFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
        hideSubcategoryFilter={true}
      />
      <CustomConfirmationModal
        isLoading={isDeleting}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDeleteConfirm}
        icon={ConfirmDeleteIcon}
        title="Delete Addon?"
        subtitle="Are you sure you want to delete this addon?"
        description="This action cannot be undone, and you won't be able to track this addon anywhere. This will remove the addon permanently."
        buttonText="Confirm"
      />
      <FormModal
        title="Restock Addon Variants"
        size="2xl"
        isOpen={isRestockOpen}
        onConfirm={handleRestockSubmit} // This already uses variants state
        onClose={() => {
          setSelectedId(null);
          setVariants([]); // Optional: clear variants on close
          onRestockClose();
        }}
        isLoading={isRestockLoading}
        children={
          <RestockForm
            product={selectedProduct}
            variants={variants}
            setVariants={setVariants}
            onSubmit={handleRestockSubmit} // Pass the submit handler
          />
        }
      />
    </Box>
  );
};
